import {
  withBooleanFeatureFlag,
  Property,
  extendAvailableUnits,
  reduceAvailableUnits,
} from '@greystar/lib';
import { AvailableUnitExtended, MonthlyFees } from '@greystar/lib/src/types/property-types';
import { PropertyDetailsFloorplansProps, PropertyFloorPlans } from '@greystar/ui';
import {
  GetStaticComponentProps,
  useSitecoreContext,
  Field,
} from '@sitecore-jss/sitecore-jss-nextjs';
import React, { JSX } from 'react';

interface PropertyDetailsFloorplansPropsExtended extends PropertyDetailsFloorplansProps {
  fields: {
    title: Field<string>;
    filterLabel: Field<string>;
    noResultLabel: Field<string>;
    filterAdjustLabel: Field<string>;
  };
}

const PropertyDetailsFloorPlans = (props: PropertyDetailsFloorplansPropsExtended): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  if (!props.data?.availableUnits?.length || !props.data?.fees?.length) {
    return <></>;
  }

  return (
    <PropertyFloorPlans
      rendering={props.rendering}
      params={props.params}
      title={props.fields?.title?.value ?? 'Floor plans'}
      filterLabel={props.fields?.filterLabel?.value ?? 'All filters'}
      fields={{
        noResultLabel: props.fields?.noResultLabel ?? { value: 'No Results' },
        filterAdjustLabel: props.fields?.filterAdjustLabel ?? {
          value: 'Try removing or adjusting your filters',
        },
      }}
      availableUnits={props.data.availableUnits}
      propertyId={props.data.propertyId}
      propertyName={props.data.propertyName}
      propertyLocation={props.data.propertyLocation}
      isPageEditing={sitecoreContext.pageEditing}
    />
  );
};

export const getStaticProps: GetStaticComponentProps = async (
  rendering,
  layoutData
): Promise<PropertyDetailsFloorplansPropsExtended> => {
  const props = rendering as unknown as PropertyDetailsFloorplansProps;
  const property = layoutData.sitecore.context.property as Property & MonthlyFees;

  if (!property?.floorplans || !property?.availableUnits) {
    return {
      ...props,
      data: {
        notFound: true,
        availableUnits: [],
        fees: null,
        propertyId: null,
        propertyName: null,
        propertyLocation: null,
      },
    };
  }

  const monthlyFees: MonthlyFees = {
    requiredMonthlyFeesMin: property.requiredMonthlyFeesMin,
    requiredMonthlyFeesMax: property.requiredMonthlyFeesMax,
  };

  const units = extendAvailableUnits({
    floorplans: property.floorplans,
    monthlyFees,
    units: property.availableUnits,
  });

  return {
    ...props,
    data: {
      notFound: false,
      availableUnits: reduceAvailableUnits({
        units,
        removeRentMatrix: true,
      }) as AvailableUnitExtended[],
      fees: property.fees ?? null,
      propertyId: property.id,
      propertyName: property.name,
      propertyLocation: property.location,
    },
  };
};

export default withBooleanFeatureFlag<PropertyDetailsFloorplansPropsExtended>(
  PropertyDetailsFloorPlans
);
